export type AlumniStatus = "active" | "inactive";

export interface AlumniType {
  _id: string;
  name: string;
  rollNo: string; // e.g. "CSE2019012"
  email: string;
  phone?: string;
  dept: string; // e.g. "1" (Dept ID)
  batch: string; // e.g. "1" (Batch ID)
  graduationYear: string; // e.g., "2023"
  company?: string;
  position?: string;
  location?: string;
  experience?: number;
  linkedIn?: string;
  status: AlumniStatus;
  createdAt: string;
  updatedAt: string;
}

export interface AlumniCreateType {
  name: string;
  rollNo: string;
  email: string;
  phone?: string;
  dept: string;
  batch: string;
  graduationYear: string;
  company?: string;
  position?: string;
  location?: string;
  experience?: number;
  linkedIn?: string;
  status: AlumniStatus;
}

export interface AlumniMetaType {
  pagination: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
}

export type AlumniParamsType = {
  search?: string;
  status?: AlumniStatus | "all";
  page?: number;
  limit?: number;
};

// ─── Dummy Data ───────────────────────────────────────────────────────────────

const DUMMY_ALUMNI: AlumniType[] = [
  {
    _id: "1",
    name: "Michael Chen",
    rollNo: "CSE2019012",
    email: "michael.chen@gmail.com",
    phone: "+1-555-0201",
    dept: "1",
    batch: "1",
    graduationYear: "2023",
    company: "Google",
    position: "Senior Software Engineer",
    location: "Mountain View, CA",
    experience: 3,
    linkedIn: "https://linkedin.com/in/michaelchen",
    status: "active",
    createdAt: "2023-06-01T00:00:00.000Z",
    updatedAt: "2023-06-01T00:00:00.000Z",
  },
  {
    _id: "2",
    name: "Sarah Thompson",
    rollNo: "IT2018034",
    email: "sarah.t@outlook.com",
    phone: "+1-555-0202",
    dept: "2",
    batch: "2",
    graduationYear: "2022",
    company: "Microsoft",
    position: "Product Manager",
    location: "Seattle, WA",
    experience: 4,
    linkedIn: "https://linkedin.com/in/sarahthompson",
    status: "active",
    createdAt: "2022-06-01T00:00:00.000Z",
    updatedAt: "2022-06-01T00:00:00.000Z",
  },
  {
    _id: "3",
    name: "Raj Patel",
    rollNo: "ECE2019056",
    email: "raj.patel@gmail.com",
    dept: "3",
    batch: "3",
    graduationYear: "2023",
    company: "Intel Corporation",
    position: "Hardware Engineer",
    location: "Santa Clara, CA",
    experience: 2,
    status: "active",
    createdAt: "2023-06-01T00:00:00.000Z",
    updatedAt: "2023-06-01T00:00:00.000Z",
  },
];

// ─── Helpers ──────────────────────────────────────────────────────────────────

const simulateDelay = (ms = 400) =>
  new Promise((resolve) => setTimeout(resolve, ms));

const applyFilters = (
  alumni: AlumniType[],
  filters: AlumniParamsType
): { data: AlumniType[]; total: number } => {
  let result = [...alumni];

  if (filters.search) {
    const q = filters.search.toLowerCase();
    result = result.filter(
      (a) =>
        a.name.toLowerCase().includes(q) ||
        a.rollNo.toLowerCase().includes(q) ||
        (a.company && a.company.toLowerCase().includes(q)) ||
        a.email.toLowerCase().includes(q)
    );
  }

  if (filters.status && filters.status !== "all") {
    result = result.filter((a) => a.status === filters.status);
  }

  const total = result.length;
  const page = filters.page ?? 1;
  const limit = filters.limit ?? 10;
  result = result.slice((page - 1) * limit, page * limit);

  return { data: result, total };
};

// ─── Service Functions ────────────────────────────────────────────────────────

export const fetchAllAlumni = async (filters: AlumniParamsType) => {
  await simulateDelay();
  // TODO: const res = await axiosInstance.get("/alumni/", { params: filters });
  const limit = filters.limit ?? 10;
  const page = filters.page ?? 1;
  const { data, total } = applyFilters(DUMMY_ALUMNI, filters);
  return {
    alumni: data,
    metaData: {
      pagination: { total, page, limit, totalPages: Math.ceil(total / limit) },
    } as AlumniMetaType,
  };
};

export const fetchAlumniById = async (_id: string) => {
  await simulateDelay();
  // TODO: const res = await axiosInstance.get(`/alumni/${_id}`);
  const alumni = DUMMY_ALUMNI.find((a) => a._id === _id);
  if (!alumni) throw new Error("Alumni not found");
  return alumni;
};

export const createAlumni = async (data: AlumniCreateType) => {
  await simulateDelay();
  // TODO: const res = await axiosInstance.post("/alumni/", data);
  const newAlumni: AlumniType = {
    ...data,
    _id: String(Date.now()),
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  };
  DUMMY_ALUMNI.push(newAlumni);
  return newAlumni;
};

export const updateAlumni = async (_id: string, data: AlumniCreateType) => {
  await simulateDelay();
  // TODO: const res = await axiosInstance.put(`/alumni/${_id}`, data);
  const index = DUMMY_ALUMNI.findIndex((a) => a._id === _id);
  if (index === -1) throw new Error("Alumni not found");
  DUMMY_ALUMNI[index] = {
    ...DUMMY_ALUMNI[index],
    ...data,
    updatedAt: new Date().toISOString(),
  };
  return DUMMY_ALUMNI[index];
};

export const deleteAlumni = async (_id: string) => {
  await simulateDelay();
  // TODO: const res = await axiosInstance.delete(`/alumni/${_id}`);
  const index = DUMMY_ALUMNI.findIndex((a) => a._id === _id);
  if (index === -1) throw new Error("Alumni not found");
  const [deleted] = DUMMY_ALUMNI.splice(index, 1);
  return deleted;
};
